/**
 * ReadingLevelToggle — Basic / Intermediate / Advanced pill switch.
 *
 * Shared by the Investment Strategy questionnaire and the Psychometric
 * Baseline (2026-09-01 directive) so both default to plain, 16-year-old
 * -level wording and let the user opt into more technical phrasing.
 * Persists the choice to the user's account via `/prefs/reading-level`
 * so it follows them across devices.
 */
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LEVELS = [
    { id: "basic",        label: "Basic" },
    { id: "intermediate", label: "Intermediate" },
    { id: "advanced",     label: "Advanced" },
];

export default function ReadingLevelToggle({ value, onChange }) {
    const { refresh } = useAuth();

    async function pick(level) {
        if (level === value) return;
        onChange(level);
        try {
            await api.post("/prefs/reading-level", { level });
            await refresh?.();
        } catch { /* non-critical — the wizard already switched locally */ }
    }

    return (
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 p-1" data-testid="reading-level-toggle">
            {LEVELS.map((l) => (
                <button
                    key={l.id}
                    type="button"
                    onClick={() => pick(l.id)}
                    data-testid={`reading-level-${l.id}`}
                    data-selected={value === l.id}
                    className={
                        "px-3 py-1 rounded-full text-xs font-semibold transition-colors " +
                        (value === l.id
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:text-foreground")
                    }
                >
                    {l.label}
                </button>
            ))}
        </div>
    );
}
